/**
 * Accessibility components for WCAG 2.1 AA compliance
 * - SkipLink: Skip navigation for keyboard users
 * - VisuallyHidden: Content for screen readers only
 * - LiveRegion: Announce dynamic content changes
 * - FocusTrap: Trap focus within modals/dialogs
 */

import { useEffect, useRef, type ReactNode } from 'react';

// =============================================================================
// Skip Link - Jump to main content
// =============================================================================

interface SkipLinkProps {
  href?: string;
  children?: ReactNode;
}

/**
 * Skip navigation link for keyboard users
 * Visible only when focused (Tab key)
 *
 * @example
 * <SkipLink href="#main-content">Skip to main content</SkipLink>
 */
export function SkipLink({ href = '#main-content', children = 'Skip to main content' }: SkipLinkProps) {
  return (
    <a
      href={href}
      className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[9999] focus:px-4 focus:py-2 focus:bg-accent-600 focus:text-white focus:rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-400 focus:ring-offset-2 focus:ring-offset-gallery-950"
    >
      {children}
    </a>
  );
}

// =============================================================================
// Visually Hidden - Screen reader only content
// =============================================================================

interface VisuallyHiddenProps {
  children: ReactNode;
  as?: 'span' | 'div' | 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

/**
 * Content that is hidden visually but accessible to screen readers
 *
 * @example
 * <button>
 *   <Icon />
 *   <VisuallyHidden>Close menu</VisuallyHidden>
 * </button>
 */
export function VisuallyHidden({ children, as: Component = 'span' }: VisuallyHiddenProps) {
  return <Component className="sr-only">{children}</Component>;
}

// =============================================================================
// Live Region - Announce dynamic changes
// =============================================================================

interface LiveRegionProps {
  children: ReactNode;
  /**
   * 'polite' waits for user pause, 'assertive' interrupts immediately
   */
  politeness?: 'polite' | 'assertive';
  /**
   * What parts of the region are announced
   */
  atomic?: boolean;
  className?: string;
}

/**
 * ARIA live region for announcing dynamic content changes
 *
 * @example
 * <LiveRegion>
 *   {isLoading ? 'Loading galleries...' : `${count} galleries found`}
 * </LiveRegion>
 */
export function LiveRegion({
  children,
  politeness = 'polite',
  atomic = true,
  className = '',
}: LiveRegionProps) {
  return (
    <div
      role="status"
      aria-live={politeness}
      aria-atomic={atomic}
      className={`sr-only ${className}`}
    >
      {children}
    </div>
  );
}

// =============================================================================
// Announcement - Temporary screen reader announcement
// =============================================================================

interface AnnouncementProps {
  message: string;
  politeness?: 'polite' | 'assertive';
}

/**
 * Temporarily announce a message to screen readers
 * Useful for form submissions, loading states, etc.
 *
 * @example
 * {showSuccess && <Announcement message="Gallery created successfully" />}
 */
export function Announcement({ message, politeness = 'polite' }: AnnouncementProps) {
  return (
    <div
      role="status"
      aria-live={politeness}
      aria-atomic="true"
      className="sr-only"
    >
      {message}
    </div>
  );
}

// =============================================================================
// Focus Trap - Keep focus within container (for modals)
// =============================================================================

interface FocusTrapProps {
  children: ReactNode;
  active?: boolean;
  onEscape?: () => void;
}

/**
 * Traps keyboard focus within a container (for modals, dialogs)
 *
 * @example
 * <FocusTrap active={isOpen} onEscape={() => setIsOpen(false)}>
 *   <Dialog>...</Dialog>
 * </FocusTrap>
 */
export function FocusTrap({ children, active = true, onEscape }: FocusTrapProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!active) return;

    const container = containerRef.current;
    if (!container) return;

    // Find all focusable elements
    const getFocusableElements = () => {
      return container.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
    };

    // Focus first element on mount
    const focusableElements = getFocusableElements();
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }

    // Handle Tab key to trap focus
    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape' && onEscape) {
        onEscape();
        return;
      }

      if (e.key !== 'Tab') return;

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        // Shift+Tab: if on first, go to last
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab: if on last, go to first
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [active, onEscape]);

  return (
    <div ref={containerRef}>
      {children}
    </div>
  );
}

// =============================================================================
// Accessible Icon Button
// =============================================================================

interface IconButtonProps {
  icon: ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Button with icon and accessible label
 *
 * @example
 * <IconButton
 *   icon={<CloseIcon />}
 *   label="Close dialog"
 *   onClick={handleClose}
 * />
 */
export function IconButton({
  icon,
  label,
  onClick,
  disabled = false,
  className = '',
  size = 'md',
}: IconButtonProps) {
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={`
        ${sizeClasses[size]}
        inline-flex items-center justify-center
        rounded-lg
        bg-gallery-800 hover:bg-gallery-700
        text-gallery-200 hover:text-white
        transition-colors
        focus:outline-none focus:ring-2 focus:ring-accent-500 focus:ring-offset-2 focus:ring-offset-gallery-950
        disabled:opacity-50 disabled:cursor-not-allowed
        ${className}
      `}
    >
      {icon}
      <span className="sr-only">{label}</span>
    </button>
  );
}

// =============================================================================
// Heading with ID (for skip links and section navigation)
// =============================================================================

interface SectionHeadingProps {
  level: 1 | 2 | 3 | 4 | 5 | 6;
  id: string;
  children: ReactNode;
  className?: string;
}

/**
 * Section heading with ID for skip links and navigation
 *
 * @example
 * <SectionHeading level={2} id="galleries">
 *   Featured Galleries
 * </SectionHeading>
 */
export function SectionHeading({ level, id, children, className = '' }: SectionHeadingProps) {
  const Tag = `h${level}` as keyof JSX.IntrinsicElements;

  return (
    <Tag id={id} tabIndex={-1} className={`scroll-mt-20 outline-none ${className}`}>
      {children}
    </Tag>
  );
}

// =============================================================================
// Accessible Image
// =============================================================================

interface AccessibleImageProps {
  src: string;
  alt: string;
  /**
   * If true, image is decorative and alt should be empty
   */
  decorative?: boolean;
  className?: string;
  width?: number;
  height?: number;
  loading?: 'lazy' | 'eager';
}

/**
 * Image with proper accessibility attributes
 *
 * @example
 * <AccessibleImage
 *   src="/artwork.jpg"
 *   alt="Abstract painting with blue and gold swirls"
 * />
 */
export function AccessibleImage({
  src,
  alt,
  decorative = false,
  className = '',
  width,
  height,
  loading = 'lazy',
}: AccessibleImageProps) {
  return (
    <img
      src={src}
      alt={decorative ? '' : alt}
      aria-hidden={decorative}
      role={decorative ? 'presentation' : undefined}
      className={className}
      width={width}
      height={height}
      loading={loading}
    />
  );
}

// =============================================================================
// Link with external indicator
// =============================================================================

interface ExternalLinkProps {
  href: string;
  children: ReactNode;
  className?: string;
}

/**
 * External link with visual indicator and accessible label
 *
 * @example
 * <ExternalLink href="https://example.com">
 *   Visit Example
 * </ExternalLink>
 */
export function ExternalLink({ href, children, className = '' }: ExternalLinkProps) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-1 ${className}`}
    >
      {children}
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
        />
      </svg>
      <span className="sr-only">(opens in new tab)</span>
    </a>
  );
}

// =============================================================================
// Form Field Wrapper
// =============================================================================

interface FormFieldProps {
  label: string;
  htmlFor: string;
  required?: boolean;
  error?: string;
  hint?: string;
  children: ReactNode;
}

/**
 * Accessible form field wrapper with label, hints, and error messages
 *
 * @example
 * <FormField label="Email" htmlFor="email" required error={errors.email}>
 *   <input id="email" type="email" aria-describedby="email-error email-hint" />
 * </FormField>
 */
export function FormField({
  label,
  htmlFor,
  required = false,
  error,
  hint,
  children,
}: FormFieldProps) {
  const errorId = `${htmlFor}-error`;
  const hintId = `${htmlFor}-hint`;

  return (
    <div className="space-y-1">
      <label
        htmlFor={htmlFor}
        className="block text-sm font-medium text-gallery-200"
      >
        {label}
        {required && (
          <span className="text-red-400 ml-1" aria-hidden="true">
            *
          </span>
        )}
        {required && <span className="sr-only">(required)</span>}
      </label>

      {hint && (
        <p id={hintId} className="text-sm text-gallery-400">
          {hint}
        </p>
      )}

      {children}

      {error && (
        <p
          id={errorId}
          role="alert"
          className="text-sm text-red-400"
        >
          {error}
        </p>
      )}
    </div>
  );
}

// =============================================================================
// Keyboard Shortcut Hint
// =============================================================================

interface KeyboardHintProps {
  keys: string[];
  action: string;
}

/**
 * Display keyboard shortcut information accessibly
 *
 * @example
 * <KeyboardHint keys={['Ctrl', 'S']} action="Save" />
 */
export function KeyboardHint({ keys, action }: KeyboardHintProps) {
  return (
    <span className="inline-flex items-center gap-1 text-sm text-gallery-400">
      {keys.map((key, i) => (
        <span key={key}>
          <kbd className="px-2 py-1 bg-gallery-800 rounded text-xs font-mono">
            {key}
          </kbd>
          {i < keys.length - 1 && <span className="mx-0.5">+</span>}
        </span>
      ))}
      <span className="ml-2">{action}</span>
    </span>
  );
}
